/**
 * Session management utility for storing and retrieving user session data.
 * Uses localStorage so data persists across app restarts and backgrounding.
 */

export interface UserSession {
  token: string;
  userName: string;
  fullName: string;
  mobile: string;
  email: string;
}

const SESSION_KEY = 'saaed_user_session';
const USER_DATA_KEY = 'saaed_user_data';
const CREDENTIALS_KEY = 'saaed_auto_login';

// ─── Session (JWT) ────────────────────────────────────────────────────────────

/**
 * Saves user session data to localStorage (persists across app restarts).
 */
export function saveSession(session: UserSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));

    const userData = {
      username: session.userName,
      fullName: session.fullName,
      mobileNumber: session.mobile,
    };
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

    if (!session.token) {
      console.warn('Warning: Session saved but token is empty');
    } else {
      console.log('✅ Session saved successfully with token');
    }
  } catch (error) {
    console.error('Failed to save session:', error);
    throw error;
  }
}

export function getSession(): UserSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserSession;
  } catch (error) {
    console.error('Failed to retrieve session:', error);
    return null;
  }
}

export function isAuthenticated(): boolean {
  const session = getSession();
  return session !== null && !!session.token;
}

export function getToken(): string | null {
  return getSession()?.token ?? null;
}

export function getFullSession(): UserSession | null {
  return getSession();
}

export function getUserData(): { username: string; fullName: string; mobileNumber: string } | null {
  const session = getSession();
  if (!session) return null;
  return {
    username: session.userName,
    fullName: session.fullName || '',
    mobileNumber: session.mobile,
  };
}

/**
 * Clears only the JWT session (keeps saved credentials for auto-login).
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  } catch (error) {
    console.error('Failed to clear session:', error);
  }
}

// ─── Auto-login credentials ───────────────────────────────────────────────────

/**
 * Persists login credentials for auto-login on next app open.
 * Password is base64-encoded (obfuscated, not encrypted).
 */
export function saveCredentials(mobile: string, password: string): void {
  try {
    const payload = {
      mobile,
      pwd: btoa(unescape(encodeURIComponent(password))),
    };
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to save credentials:', error);
  }
}

/**
 * Returns saved credentials for auto-login, or null if none are stored.
 */
export function getSavedCredentials(): { mobile: string; password: string } | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS_KEY);
    if (!raw) return null;
    const { mobile, pwd } = JSON.parse(raw);
    return { mobile, password: decodeURIComponent(escape(atob(pwd))) };
  } catch (error) {
    console.error('Failed to retrieve credentials:', error);
    return null;
  }
}

/**
 * Removes saved credentials (call on explicit user logout).
 */
export function clearCredentials(): void {
  try {
    localStorage.removeItem(CREDENTIALS_KEY);
  } catch (error) {
    console.error('Failed to clear credentials:', error);
  }
}

/**
 * Clears both the session AND saved credentials (explicit logout).
 */
export function clearAllAuth(): void {
  clearSession();
  clearCredentials();
  clearPreferences();
}

// ─── App preferences (voice gender etc.) ─────────────────────────────────────

const PREFERENCES_KEY = 'saaed_app_preferences';

export interface AppPreferences {
  voiceGender: 'male' | 'female';
}

/**
 * Persists user app preferences so the app can fast-resume without the welcome screen.
 */
export function savePreferences(prefs: AppPreferences): void {
  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
  } catch (error) {
    console.error('Failed to save preferences:', error);
  }
}

/**
 * Returns saved app preferences or null if not set.
 */
export function getSavedPreferences(): AppPreferences | null {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AppPreferences;
  } catch (error) {
    console.error('Failed to retrieve preferences:', error);
    return null;
  }
}

export function clearPreferences(): void {
  try {
    localStorage.removeItem(PREFERENCES_KEY);
  } catch (error) {
    console.error('Failed to clear preferences:', error);
  }
}
